import { hasPathPermission } from '@/hooks/useBackendMenu'
import { cn } from '@/lib/utils'
import localeText from '@/locales/datagrid'
import { onlyUnique } from '@acme/common'
import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import useDataGridPro from '../../hooks/useDataGridPro'
import useDefaultFilterModel from '../../hooks/useDefaultFilterModel'
import useDefaultSelectionModel from '../../hooks/useDefaultSelectionModel'
import useDefaultSortModel from '../../hooks/useDefaultSortModel'
import { TableApiProvider } from '../../utils/table-context'
import {
    GridCellEditCommitParams,
    GridColDefPro,
    GridRowHeightParams,
    GridRowParams,
    TColumnGrouping,
    TDensity,
    ValidRowModel,
} from '../../utils/types'
import ColumnVisibility from '../ColumnVisibility'
import GridToolbarContainer from '../CustomGridToolBar/Container'
import GridToolbarLeftSide from '../CustomGridToolBar/LeftSide'
import GridToolbarRightSide from '../CustomGridToolBar/RightSide'
import FilterPanelPro from '../FilterPanelPro'
import QuickFilter from '../QuickFilter'
import { TableFooter, TableFooterContainer } from './Footer'
import MainTable, { ApiRef } from './MainTable'
import normalizePageSize from './normalizePageSize'
import TableOverlay from './table-overlay'

interface ITableComponents {
    Toolbar?: () => React.ReactNode
    Footer?: () => React.ReactNode
    ErrorOverlay?: (err: Error) => React.ReactNode
}

interface ITableSlots {
    Toolbar?: () => React.ReactNode
    Footer?: () => React.ReactNode
}

interface IToolbarProps {
    showQuickFilter?: boolean
    hideToolbar?: boolean
    hideFilterButton?: boolean
    hideDensitySelector?: boolean
    hideExportButton?: boolean
    showVisibilityButton?: boolean
    exportButtonProps?: {
        fileName?: string
        children?: React.ReactNode
    }
}

export interface IDataTableProps<TData extends ValidRowModel = any> {
    onAdd?: (() => void) | string | boolean | React.ReactNode
    columns: GridColDefPro<TData>[]
    rows?: TData[]
    style?: React.CSSProperties
    className?: string
    density?: TDensity
    hideFooter?: boolean
    apiRef?: ApiRef
    checkboxSelection?: boolean
    enableRowExpanding?: boolean

    ToolbarProps?: IToolbarProps
    /** Children passado para cada componente */
    components?: ITableComponents
    /** Componente que vai renderizar no lugar do padrão */
    slots?: ITableSlots

    filterModel?: ReturnType<typeof useDefaultFilterModel>[0]
    onFilterModelChange?: ReturnType<typeof useDefaultFilterModel>[1]
    sortModel?: ReturnType<typeof useDefaultSortModel>[0]
    onSortModelChange?: ReturnType<typeof useDefaultSortModel>[1]
    selectionModel?: ReturnType<typeof useDefaultSelectionModel>[0]
    onSelectionModelChange?: ReturnType<typeof useDefaultSelectionModel>[1]
    columnVisibilityModel?: {
        [key: string]: boolean
    }
    columnGroupingModel?: TColumnGrouping[]

    /**
     * Número de linhas por página (fixo),
     * utilzie `pageSizeOptions` e `defaultPageSize` para controle dinâmico
     *
     * `-1` / `Infinity` para exibir todas as linhas
     */
    pageSize?: number
    /** `-1` / `Infinity` para exibir todas as linhas */
    defaultPageSize?: number
    /** Página inicial (0-based). Usada ao montar o DataGrid com paginação vinda da URL. */
    defaultPageIndex?: number
    pageSizeOptions?: Array<number | { value: number; label: string }>

    error?: boolean | Error
    loading: boolean

    onRowClick?: (params: GridRowParams<TData>) => any
    onCellEditCommit?: (params: GridCellEditCommitParams) => any

    headerHeight?: number
    rowHeight?: number
    getRowHeight?: (params: GridRowHeightParams<TData>) => number
    getRowClassName?: (
        params: GridRowParams<TData>,
    ) => React.HTMLAttributes<HTMLTableRowElement>['className']
    /** Função para obter as linhas a serem exportadas */
    getRowsToExport?: (formattedRows: ValidRowModel[]) => TData[]
    /**
     * Total de linhas no servidor.
     * Quando presente, ativa modo server-side:
     * - manualPagination: true no TanStack Table
     * - Footer usa totalRows em vez do rowModel length
     * - FilterModelPro bypassed (servidor filtra)
     */
    totalRows?: number
    /** Chamado quando page ou pageSize muda em modo server-side */
    onPaginationChange?: (pagination: {
        pageIndex: number
        pageSize: number
    }) => void
    /** Indica se o DataTable está filtrando, organizando e paginando server-side */
    serverSide?: boolean
    /** Total de linhas selecionadas (para selectAll server-side) */
    totalSelected?: number
}

export function DataTable({
    onAdd,
    ToolbarProps,
    columns,
    density,
    filterModel,
    onFilterModelChange,
    defaultPageSize = 25,
    defaultPageIndex = 0,
    pageSize,
    rows,
    style,
    components = {},
    slots = {},
    hideFooter,
    apiRef,
    loading,
    error,
    onRowClick,
    onCellEditCommit,
    checkboxSelection,
    selectionModel,
    onSelectionModelChange,
    columnVisibilityModel,
    headerHeight,
    rowHeight,
    getRowHeight,
    getRowClassName,
    getRowsToExport,
    sortModel,
    onSortModelChange,
    enableRowExpanding,
    columnGroupingModel,
    pageSizeOptions,
    className,
    totalRows,
    onPaginationChange,
    totalSelected,
}: IDataTableProps) {
    const defaultRef = React.useRef(null)
    const tableApiRef: IDataTableProps['apiRef'] =
        apiRef !== undefined ? apiRef : defaultRef
    const { pathname } = useLocation()
    const navigate = useNavigate()

    if (hideFooter) defaultPageSize = Infinity
    defaultPageSize = normalizePageSize(defaultPageSize)

    pageSizeOptions ??= [
        ...[10, 25, 50, 100]
            .concat(
                defaultPageSize === Number.MAX_SAFE_INTEGER
                    ? []
                    : [defaultPageSize],
            )
            .filter(onlyUnique)
            .sort((a, b) => a - b),
        {
            value: Number.MAX_SAFE_INTEGER,
            label: localeText.footerAllRows,
        },
    ]

    const props = useDataGridPro({
        density,
        rows,
        initialFilterModel: { items: [] },
        columns,
        enableRowExpanding,
        pageSize: hideFooter ? Infinity : pageSize,
        pageSizeOptions,
        defaultPageSize,
        defaultPageIndex,
        isLoading: loading,
        onRowClick,
        onCellEditCommit,
        controlledFilterModel: filterModel,
        setFilterModel: onFilterModelChange,
        selectionModel,
        onSelectionModelChange,
        checkboxSelection,
        columnVisibilityModel,
        heightControls: { headerHeight, rowHeight, getRowHeight },
        getRowClassName,
        getRowsToExport,
        sortModel,
        onSortModelChange,
        columnGroupingModel,
        totalRows,
        onPaginationChange,
    })

    if (typeof onAdd === 'string') {
        const path = structuredClone(onAdd)
        onAdd = () => navigate(path)
    }

    if (typeof onAdd === 'boolean') onAdd = () => navigate('adicionar')

    if (!hasPathPermission('create', pathname) && !React.isValidElement(onAdd))
        onAdd = undefined

    const MemoizedPanel = React.useMemo(
        () => (props.isFiltroVisivel ? <FilterPanelPro /> : null),
        [
            props.filterLength,
            JSON.stringify(props.filterModelPro.items),
            props.isFiltroVisivel,
        ],
    )

    const MemoizedVisibilidade = React.useMemo(
        () => (props.visibilidadeColunas ? <ColumnVisibility /> : null),
        [props.visibilidadeColunas, columns.map(c => c.hide)],
    )

    const MemoizedToolbarProps = React.useMemo(
            () => ToolbarProps || {},
            [JSON.stringify(ToolbarProps)],
        ),
        MemoizedQuickFilter = React.useMemo(
            () =>
                MemoizedToolbarProps.showQuickFilter ? <QuickFilter /> : null,
            [MemoizedToolbarProps.showQuickFilter],
        )

    return (
        <TableApiProvider
            {...props}
            table={tableApiRef.current}
            totalSelected={totalSelected}
        >
            <div
                key={`${props.isFiltroVisivel}${props.visibilidadeColunas}`}
                className={cn(
                    'size-full overflow-hidden flex flex-col content-between flex-1',
                    'transition-all duration-150',
                    className,
                )}
                style={style}
            >
                <GridToolbarContainer
                    hidden={MemoizedToolbarProps.hideToolbar || !!slots.Toolbar}
                >
                    <GridToolbarLeftSide
                        buttonsProps={{
                            exportButtonProps: {
                                children:
                                    MemoizedToolbarProps.exportButtonProps
                                        ?.children,
                                fileName:
                                    MemoizedToolbarProps.exportButtonProps
                                        ?.fileName ?? 'exportacao',
                            },
                            hideDensitySelector:
                                !!MemoizedToolbarProps.hideDensitySelector,
                            hideExportButton:
                                !!MemoizedToolbarProps.hideExportButton,
                            hideFilterButton:
                                !!MemoizedToolbarProps.hideFilterButton,
                            showVisibilityButton:
                                !!MemoizedToolbarProps.showVisibilityButton,
                        }}
                    >
                        {components.Toolbar ? <components.Toolbar /> : null}
                    </GridToolbarLeftSide>
                    <GridToolbarRightSide onAdd={onAdd}>
                        {MemoizedQuickFilter}
                    </GridToolbarRightSide>
                </GridToolbarContainer>
                {!!slots.Toolbar && <slots.Toolbar />}
                {MemoizedVisibilidade}
                {MemoizedPanel}
                <div className="overflow-auto border flex-1 min-h-0">
                    <TableOverlay
                        errorOverlay={components.ErrorOverlay}
                        error={error}
                    >
                        <MainTable apiRef={tableApiRef} />
                    </TableOverlay>
                </div>
                <TableFooterContainer hidden={hideFooter || !!slots.Footer}>
                    {components.Footer ? <components.Footer /> : null}
                    <TableFooter />
                </TableFooterContainer>
                {!!slots.Footer && <slots.Footer />}
            </div>
        </TableApiProvider>
    )
}
